/**
 * UserProfilePage — [UC-038] View Own Profile
 *
 * Read-only per UC-038 spec. Edit is intentionally NOT implemented here —
 * email-change flow needs a verification loop that's out of scope for this
 * UC; sensitive mutations (email, password) have their own dedicated pages
 * (UserPasswordChangePage) and flows with proper confirmation steps.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../core/api-client';
import { fmtMoney } from '../utils/format';

interface UserProfile {
  userId: number;
  name: string;
  surname: string;
  email: string;
  role: string;
  status: string;
  creditBalance: number;
  registrationDate: string;
}

type ApiUserItem = {
  userId?: number;
  user_id?: number;
  name: string;
  surname: string;
  email: string;
  role: string;
  status: string;
  creditBalance?: number;
  credit_balance?: number;
  registrationDate?: string;
  registration_date?: string;
};

function normaliseProfile(raw: ApiUserItem): UserProfile {
  return {
    userId: (raw.userId ?? raw.user_id) as number,
    name: raw.name,
    surname: raw.surname,
    email: raw.email,
    role: raw.role,
    status: raw.status,
    creditBalance: Number(raw.creditBalance ?? raw.credit_balance ?? 0),
    registrationDate: (raw.registrationDate ?? raw.registration_date ?? '') as string,
  };
}

export function UserProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // GET /users/me is paginated (1 row) for legacy reasons.
      const response = await api.get<{ items?: ApiUserItem[] }>('/users/me');
      const first = response.items?.[0];
      if (!first) {
        setError(t('userProfile.notFound', 'Profile not found'));
        return;
      }
      setProfile(normaliseProfile(first));
    } catch (e) {
      console.error('Failed to load profile:', e);
      setError(t('userProfile.loadError', 'Failed to load profile'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const initials = useMemo(() => {
    if (!profile) return '';
    return `${profile.name?.charAt(0) ?? ''}${profile.surname?.charAt(0) ?? ''}`.toUpperCase();
  }, [profile]);

  const memberSince = useMemo(() => {
    if (!profile?.registrationDate) return '';
    try {
      return new Date(profile.registrationDate).toLocaleDateString();
    } catch {
      return profile.registrationDate;
    }
  }, [profile]);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <div className="bg-red-600/15 border border-red-600/25 rounded-xl p-4 max-w-xl">
          <p className="text-red-700 text-sm">{error || t('userProfile.notFound', 'Profile not found')}</p>
          <button
            onClick={loadProfile}
            type="button"
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
          >
            {t('userProfile.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">
          {t('userProfile.title', 'Profile')}
        </h1>
        <p className="mt-1.5 text-base sm:text-sm text-[var(--text-secondary)]">
          {t('userProfile.subtitle', 'Your account details')}
        </p>
      </div>

      {/* Two-column layout on desktop so the page fills the sidebar'd
          content width without stretching the rows to unreadable widths.
          Stacks on mobile. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Identity card */}
        <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-5 sm:p-6">
          <div className="flex items-center gap-4 sm:gap-5">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[var(--accent-primary)] flex items-center justify-center flex-shrink-0">
              <span className="text-xl sm:text-2xl font-bold text-[var(--accent-text)]">
                {initials}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] break-words">
                {profile.name} {profile.surname}
              </div>
              <div className="text-sm text-[var(--text-secondary)] break-all">{profile.email}</div>
            </div>
          </div>
        </div>

        {/* Account details — role intentionally omitted (internal), email
            sits on the identity card, so this card is status + credits +
            tenure. */}
        <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
            {t('userProfile.accountDetails', 'Account details')}
          </h2>
          <dl className="divide-y divide-[var(--border-subtle)]">
            <div className="py-2 flex items-center justify-between gap-3">
              <dt className="text-sm text-[var(--text-secondary)]">{t('userProfile.status', 'Status')}</dt>
              <dd>
                <span
                  className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full border ${
                    profile.status === 'Active'
                      ? 'bg-green-600/15 text-green-700 dark:text-green-500 border-green-600/25'
                      : 'bg-red-600/15 text-red-700 dark:text-red-500 border-red-600/25'
                  }`}
                >
                  {profile.status}
                </span>
              </dd>
            </div>
            <div className="py-2 flex items-center justify-between gap-3">
              <dt className="text-sm text-[var(--text-secondary)]">{t('userProfile.creditBalance', 'Credit balance')}</dt>
              <dd className="text-sm font-semibold text-[var(--text-primary)] text-right">
                {fmtMoney(profile.creditBalance, 2)}
              </dd>
            </div>
            {memberSince && (
              <div className="py-2 flex items-center justify-between gap-3">
                <dt className="text-sm text-[var(--text-secondary)]">{t('userProfile.memberSince', 'Member since')}</dt>
                <dd className="text-sm text-[var(--text-primary)] text-right">{memberSince}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Actions — change password exists as a separate page; email change is
          intentionally NOT exposed here (needs verification-loop flow). */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate('/user-dashboard')}
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
        >
          {t('userProfile.back', 'Back')}
        </button>
        <button
          type="button"
          onClick={() => navigate('/user-password-change')}
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors"
        >
          {t('userProfile.changePassword', 'Change password')}
        </button>
      </div>
    </div>
  );
}

export default UserProfilePage;
